import {
  ChangeEvent,
  ForwardedRef,
  InputHTMLAttributes,
  forwardRef,
  useState,
} from 'react';
import { Tooltip } from '@mui/material';
import { Controller, FieldValues, RegisterOptions } from 'react-hook-form';
import {
  convertToSnakeCase,
  getBorderProps,
  getFontSize,
  getNewMarginStyles,
  getPaddingStyles,
} from '../../utils/helper';
import { IBorderValueProps } from '../../puck/reusable-props/Border';
import { PaddingType } from '../../puck/reusable-props/padding';
import { MarginType } from '../../puck/reusable-props/NewMargin';
import { IOutlineValueProps } from '../../puck/reusable-props/Outline';
interface ITextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  className?: string;
  labelKey?: string;
  valueKey?: string;
  defaultOption?: string;
  textColor?: {
    color: string;
    isInherit: boolean;
  };
  backgroundColor?: {
    color: string;
    isInherit: boolean;
  };
  placeHolderColor?: string;
  placeHolderFontSize?: string;
  placeHolderLineHeight?: string;
  currentDeviceType?: string;
  PlaceHolderTextAlign?: string;
  placeHolderweight?: string;
  placeHolderFontFamily?: string;
  placeHolderLetterSpace?: string;
  placeholdercolor?: string;
  uniqueName?: string;
  fieldHeight?: string;
  radiusBorder: IBorderValueProps;
  error?: string;
  tooltipTitle?: string;
  InputIcon?: React.ComponentType;
  outline?: IOutlineValueProps;
  header?: {
    text: string;
    hide: boolean;
    padding: PaddingType;
    margin: MarginType;
  };
}

export const TextField = forwardRef(function TextFieldInternal(
  {
    label,
    className,
    error,
    InputIcon,
    fieldHeight,
    tooltipTitle,
    placeholder,
    placeHolderColor,
    placeHolderFontSize,
    textColor,
    backgroundColor,
    currentDeviceType,
    placeHolderweight,
    placeHolderLineHeight,
    PlaceHolderTextAlign,
    placeHolderFontFamily,
    placeHolderLetterSpace,
    radiusBorder,
    uniqueName,
    header,
    outline,
    ...rest
  }: ITextFieldProps,
  ref: ForwardedRef<HTMLInputElement>,
) {
  //
  const placeholderclass = convertToSnakeCase(uniqueName || placeholder || ''); //convertTosnakeCase is a function that convert the string to snake case
  placeHolderFontSize = getFontSize(placeHolderFontSize); // getFontSize is a function that return the font size in px
  const [isInputFocused, setisInputFocused] = useState(false);

  const borderStyles =
    isInputFocused && outline
      ? getBorderProps(outline, currentDeviceType || 'desktop')
      : getBorderProps(radiusBorder, currentDeviceType || 'desktop');

  const forbiddenSymbolsRegex = /[^\x20-\x7E]/g;

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const clipboardData = e.clipboardData.getData('text');
    const sanitizedData = clipboardData.replace(forbiddenSymbolsRegex, '');

    const input = e.currentTarget;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const currentValue = input.value;

    const newValue =
      currentValue.slice(0, start) + sanitizedData + currentValue.slice(end);

    if (input instanceof HTMLInputElement) {
      input.value = newValue;
      const syntheticEvent = {
        ...e,
        target: input,
        currentTarget: input,
      } as React.ChangeEvent<HTMLInputElement>;
      rest?.onChange?.(syntheticEvent);
    }
  };

  return (
    <div className={`${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <div>
        <div className="text-editor-content">
          {header && header.hide && (
            <div
              style={{
                ...getPaddingStyles(
                  header.padding,
                  currentDeviceType || 'desktop',
                ),
                ...getNewMarginStyles(
                  header.margin,
                  currentDeviceType || 'desktop',
                ),
              }}
              className="text-sm mb-1 ml-2"
              dangerouslySetInnerHTML={{ __html: header.text }}
            />
          )}
          <div className="relative">
            <input
              style={{
                height: fieldHeight
                  ? isNaN(+fieldHeight)
                    ? fieldHeight
                    : `${+fieldHeight}px`
                  : '48px',
                color: textColor?.isInherit ? 'inherit' : textColor?.color,
                backgroundColor: backgroundColor?.isInherit
                  ? 'inherit'
                  : backgroundColor?.color,
                ...borderStyles,
              }}
              type="text"
              onKeyDown={e => e.stopPropagation()}
              onFocus={() => setisInputFocused(true)}
              onBlur={() => setisInputFocused(false)}
              onPaste={handlePaste}
              ref={ref}
              className={`
              block w-full rounded-md border-gray-300 shadow-sm 
              focus:border-indigo-500 focus:ring-indigo-500 
              sm:text-sm pl-3 pr-[22.5px] custom-placeholder-${placeholderclass}  : isInputFocused ? outline?.borderStyle?.[currentDeviceType || 'desktop'] : radiusBorder?.borderStyle?.[currentDeviceType || 'desktop']}`}
              placeholder={placeholder}
              {...rest}
            />
            {InputIcon && (
              <Tooltip title={tooltipTitle} arrow>
                <span
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                  aria-hidden="true">
                  <InputIcon />
                </span>
              </Tooltip>
            )}
          </div>
          <style>
            {`
         .custom-placeholder-${placeholderclass}::placeholder{
            color: ${placeHolderColor};
            font-size: ${placeHolderFontSize};
            font-weight: ${placeHolderweight};
            height: ${placeHolderLineHeight};
            text-align: ${PlaceHolderTextAlign};
            font-family:${placeHolderFontFamily};
            letter-spacing: ${placeHolderLetterSpace}px;
          } 
        `}
          </style>
        </div>
        {error && (
          <div
            className="error-message"
            style={{ color: 'var(--error-color)' }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
});

interface ITextFieldControlProps extends ITextFieldProps {
  name: string;
  defaultValue?: string;
  rules?: Omit<
    RegisterOptions<FieldValues, string>,
    'disabled' | 'setValueAs' | 'valueAsNumber' | 'valueAsDate'
  >;
  valueFormatter?: (_value: string) => string;
  onChangeHandler?: (_e: ChangeEvent<HTMLInputElement>) => string;
}
export const TextFieldControl = ({
  name,
  rules,
  valueFormatter,
  onChangeHandler,
  defaultValue,
  fieldHeight,
  placeHolderColor,
  placeholder,
  header,
  currentDeviceType,
  placeHolderFontSize,
  textColor,
  backgroundColor,
  placeHolderweight,
  PlaceHolderTextAlign,
  placeHolderFontFamily,
  placeHolderLetterSpace,
  radiusBorder,
  ...rest
}: ITextFieldControlProps) => {
  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        return (
          <TextField
            {...rest}
            placeholder={placeholder}
            value={valueFormatter ? valueFormatter(value) : value}
            onChange={e =>
              onChange(
                onChangeHandler
                  ? onChangeHandler(e)
                  : e.target.value?.trimStart(),
              )
            }
            error={error?.message}
            fieldHeight={fieldHeight}
            placeHolderColor={placeHolderColor}
            placeHolderFontSize={placeHolderFontSize}
            placeHolderweight={placeHolderweight}
            textColor={textColor}
            backgroundColor={backgroundColor}
            PlaceHolderTextAlign={PlaceHolderTextAlign}
            placeHolderFontFamily={placeHolderFontFamily}
            placeHolderLetterSpace={placeHolderLetterSpace}
            radiusBorder={radiusBorder}
            currentDeviceType={currentDeviceType}
            header={header}
          />
        );
      }}
      rules={rules}
    />
  );
};
